import { API_BASE_URL } from '../config';

export interface ApiResponse<T> {
    status: 'success' | 'error';
    data?: T;
    message?: string;
    debug?: any;
    exists?: boolean;
}

async function internalRequest<T>(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', body?: any): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };

    const config: RequestInit = {
        method,
        headers,
        credentials: 'include',
    };

    if (body) {
        config.body = JSON.stringify(body);
    }

    // Sicherstellen, dass keine doppelten Slashes entstehen
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${baseUrl}/${cleanEndpoint}`;

    try {
        const response = await fetch(url, config);
        const text = await response.text();
        
        let json: ApiResponse<T> | null = null;
        try {
            json = JSON.parse(text);
        } catch (e) {
            console.error("API Error: Server returned non-JSON response for " + url, text);
        }

        if (!response.ok) {
            if (json && json.message) {
                throw new Error(json.message);
            }
            throw new Error(`Server Fehler (${response.status}): ${text.substring(0, 100)}`);
        }

        if (!json) {
             const snippet = text.length > 50 ? text.substring(0, 50) + "..." : text;
             throw new Error(`Ungültige Server-Antwort (kein JSON). Antwort begann mit: '${snippet}'`);
        }

        if (json.status === 'error') {
            throw new Error(json.message || 'Unbekannter API Fehler');
        }

        return json;

    } catch (error: any) {
        console.error('API Request Failed:', error);
        throw error;
    }
}

// Neue Funktion: Gibt die komplette Antwort inkl. status/exists zurück
export async function apiRequestFull<T>(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', body?: any): Promise<ApiResponse<T>> {
    return internalRequest<T>(endpoint, method, body);
}

// Standard Funktion: Gibt direkt data zurück (für Abwärtskompatibilität)
export async function apiRequest<T>(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', body?: any): Promise<T> {
    const json = await internalRequest<T>(endpoint, method, body);

    if (json.data !== undefined) {
         if (json.debug && typeof json.data === 'object' && json.data !== null && !Array.isArray(json.data)) {
             (json.data as any)._debug = json.debug;
         }
         return json.data as T;
    }

    return json as unknown as T;
}
